import { useEffect, useMemo, useRef } from 'react';
import { useDispatch, useSelector } from 'react-redux';
import {
    GetDocumentIndexStatus,
    hydrateIndexingFiles,
    markFilesIndexingFailed,
    pruneExpiredIndexingState,
} from '../redux/documentsSlice';
import { registerVdrIndexStatusPoller } from '../utils/vdrIndexStatusPollCoordinator';
import { shouldPollDocumentIndexStatus } from '../utils/vdrIndexingQueue';

/**
 * Polls index_status for in-flight VDR uploads (session queue + upload tray).
 * Uses a shared coordinator so multiple mounted routes do not multiply requests.
 */
const useVdrIndexStatusPolling = () => {
    const dispatch = useDispatch();
    const indexingFiles = useSelector(state => state?.documents?.indexingFiles || []);
    const trayItems = useSelector(state => state?.documents?.uploadTray?.items || []);
    const { selectedProject } = useSelector(state => state?.projectData);

    const pollTargets = useMemo(() => {
        const now = Date.now();
        const byId = new Map();
        indexingFiles.forEach(item => {
            if (item?.id == null) return;
            const startedAt = Number(item.startedAt) || 0;
            const row = {
                id: Number(item.id),
                wsId: item?.wsId != null ? Number(item.wsId) : null,
                startedAt,
                status: item?.status === 'failed' ? 'failed' : 'indexing',
            };
            if (!shouldPollDocumentIndexStatus(row, now)) return;
            byId.set(row.id, row);
        });
        trayItems.forEach(item => {
            if (item?.status !== 'indexing' || item?.documentId == null) {
                return;
            }
            const id = Number(item.documentId);
            const row = {
                id,
                wsId: item?.wsId != null ? Number(item.wsId) : null,
                startedAt: Number(item.startedAt) || 0,
                status: 'indexing',
            };
            if (byId.has(id) || !shouldPollDocumentIndexStatus(row, now)) return;
            byId.set(id, row);
        });
        return [...byId.values()];
    }, [indexingFiles, trayItems]);

    const pollTargetKey = useMemo(
        () =>
            pollTargets
                .map(item => `${item.id}:${item.wsId}:${item.status}:${item.startedAt}`)
                .sort()
                .join(','),
        [pollTargets],
    );

    const pollTargetsRef = useRef(pollTargets);
    pollTargetsRef.current = pollTargets;

    const projectIdRef = useRef(selectedProject?.id);
    projectIdRef.current = selectedProject?.id || localStorage.getItem('project_id');

    useEffect(() => {
        dispatch(hydrateIndexingFiles());
    }, [dispatch, selectedProject?.id]);

    useEffect(() => {
        if (!pollTargetKey) return undefined;

        const runPoll = async () => {
            const targets = pollTargetsRef.current;
            if (!targets.length) return;

            const projectId = projectIdRef.current;
            if (!projectId) return;

            dispatch(pruneExpiredIndexingState());

            const timedOutIds = [];
            const active = [];
            targets.forEach(item => {
                if (!shouldPollDocumentIndexStatus(item)) {
                    return;
                }
                if (item.wsId == null || Number.isNaN(Number(item.wsId))) {
                    timedOutIds.push(item.id);
                } else {
                    active.push(item);
                }
            });
            if (timedOutIds.length) {
                dispatch(markFilesIndexingFailed(timedOutIds));
            }
            if (!active.length) return;

            const byWs = active.reduce((acc, item) => {
                if (item.wsId == null || Number.isNaN(Number(item.wsId))) return acc;
                const key = String(item.wsId);
                if (!acc[key]) acc[key] = [];
                acc[key].push(item.id);
                return acc;
            }, {});

            await Promise.all(
                Object.entries(byWs).map(([wsId, fileIds]) =>
                    dispatch(
                        GetDocumentIndexStatus({
                            wsId,
                            fileIds,
                            projectId,
                        }),
                    ),
                ),
            );
        };

        return registerVdrIndexStatusPoller(runPoll);
    }, [dispatch, pollTargetKey]);
};

export default useVdrIndexStatusPolling;
